import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, Loader2, Check, X, Eye, Clock, CheckCircle, XCircle,
  AlertCircle, ChevronDown, Megaphone, Grid3X3, Shield
} from 'lucide-react';
import api from '../../utils/api';


const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-600', Icon: AlertCircle },
  pending_review: { label: 'Pending Review', color: 'bg-amber-50 text-amber-700 border border-amber-200', Icon: Clock },
  approved: { label: 'Approved', color: 'bg-blue-50 text-blue-600', Icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-50 text-red-600', Icon: XCircle },
  active: { label: 'Active', color: 'bg-emerald-50 text-emerald-600', Icon: Eye },
  expired: { label: 'Expired', color: 'bg-slate-100 text-slate-400', Icon: Clock },
};

const SLOTS = ['homepage_slot_1', 'homepage_slot_2', 'homepage_slot_3', 'homepage_slot_4'];

// ═══════════════════════════════════════════════════════════
// SLOT MANAGER
// ═══════════════════════════════════════════════════════════

const SlotManager = ({ slots, approvedCampaigns, onAssign, onUnassign }) => (
  <Card className="border border-slate-200 mb-6" data-testid="slot-manager">
    <CardContent className="p-5">
      <h3 className="text-base font-bold text-[#122033] mb-3 flex items-center gap-2">
        <Grid3X3 className="h-4 w-4 text-[#3BA4C5]" /> Homepage Placement Slots
      </h3>
      <p className="text-xs text-slate-400 mb-4">Assign approved campaigns to homepage slots. Only 4 slots available.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {slots.map((s, idx) => (
          <div key={s.slot} className={`border rounded-xl p-3 transition-colors ${s.campaign ? 'border-[#3BA4C5]/30 bg-[#3BA4C5]/[0.02]' : 'border-dashed border-slate-200 bg-slate-50/50'}`}
            data-testid={`slot-${s.slot}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-slate-500 uppercase">Slot {idx + 1}</span>
              {s.campaign && (
                <button onClick={() => onUnassign(s.campaign.campaign_id)}
                  className="text-[10px] text-red-400 hover:text-red-600" data-testid={`unassign-${s.slot}`}>
                  Remove
                </button>
              )}
            </div>
            {s.campaign ? (
              <div>
                <p className="text-sm font-medium text-[#122033] truncate">{s.campaign.campaign_title}</p>
                <p className="text-[10px] text-slate-400">{s.campaign.clinic_name} &middot; {s.campaign.treatment}</p>
                <Badge className={`text-[9px] mt-1 border-0 ${STATUS_CONFIG[s.campaign.status]?.color || ''}`}>
                  {STATUS_CONFIG[s.campaign.status]?.label || s.campaign.status}
                </Badge>
              </div>
            ) : (
              <div>
                <p className="text-xs text-slate-300 mb-1.5">Empty slot</p>
                {approvedCampaigns.length > 0 ? (
                  <select onChange={e => { if (e.target.value) onAssign(e.target.value, s.slot); e.target.value = ''; }}
                    className="w-full border border-slate-200 rounded-md p-1.5 text-xs"
                    data-testid={`assign-select-${s.slot}`}>
                    <option value="">Assign campaign...</option>
                    {approvedCampaigns.map(c => (
                      <option key={c.campaign_id} value={c.campaign_id}>{c.campaign_title} ({c.clinic_name})</option>
                    ))}
                  </select>
                ) : (
                  <p className="text-[10px] text-slate-300 italic">No approved campaigns to assign</p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

// ═══════════════════════════════════════════════════════════
// CAMPAIGN REVIEW CARD
// ═══════════════════════════════════════════════════════════

const ReviewCard = ({ campaign, onApprove, onReject, onEdit, expanded, onToggle }) => {
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const cfg = STATUS_CONFIG[campaign.status] || STATUS_CONFIG.draft;

  return (
    <Card className={`border transition-all ${campaign.status === 'pending_review' ? 'border-amber-200' : 'border-slate-200'}`}
      data-testid={`review-card-${campaign.campaign_id}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Image */}
          <div className="flex-none w-16 h-12 rounded-lg bg-slate-100 overflow-hidden">
            {campaign.image_url ? (
              <img src={campaign.image_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Megaphone className="h-5 w-5 text-slate-300" />
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-semibold text-[#122033] truncate">{campaign.campaign_title || 'Untitled'}</h3>
              <Badge className={`text-[9px] border-0 ${cfg.color}`}>
                <cfg.Icon className="h-3 w-3 mr-0.5" />{cfg.label}
              </Badge>
              {campaign.homepage_slot && (
                <Badge variant="outline" className="text-[9px]">{campaign.homepage_slot.replace(/_/g, ' ')}</Badge>
              )}
            </div>
            <p className="text-xs text-slate-400">
              <span className="font-medium text-slate-600">{campaign.clinic_name}</span>
              {campaign.treatment && <span> &middot; {campaign.treatment}</span>}
              {campaign.price_from > 0 && <span> &middot; From &euro;{campaign.price_from.toLocaleString()}</span>}
            </p>
            {campaign.headline && <p className="text-xs text-slate-500 mt-0.5 italic">"{campaign.headline}"</p>}

            {/* Expanded details */}
            {expanded && (
              <div className="mt-3 p-3 bg-slate-50 rounded-lg text-xs space-y-1" data-testid="campaign-details">
                {campaign.description && <p className="text-slate-600">{campaign.description}</p>}
                <p className="text-slate-400">Placement: {campaign.placement_type} &middot; Destination: {campaign.destination || 'N/A'}</p>
                <p className="text-slate-400">
                  Dates: {campaign.start_date || 'No start'} → {campaign.end_date || 'No end'}
                </p>
                {campaign.rejection_reason && <p className="text-red-500">Previous rejection: {campaign.rejection_reason}</p>}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1.5 flex-none">
            <button onClick={onToggle} className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center">
              <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
            </button>

            {campaign.status === 'pending_review' && (
              <>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs gap-1"
                  onClick={() => onApprove(campaign.campaign_id)} data-testid={`approve-${campaign.campaign_id}`}>
                  <Check className="h-3 w-3" /> Approve
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-xs gap-1 text-red-500 border-red-200 hover:bg-red-50"
                  onClick={() => setShowReject(!showReject)} data-testid={`reject-toggle-${campaign.campaign_id}`}>
                  <X className="h-3 w-3" /> Reject
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Reject form */}
        {showReject && (
          <div className="mt-3 flex gap-2 items-end" data-testid="reject-form">
            <div className="flex-1">
              <label className="text-[10px] text-slate-400 mb-0.5 block">Rejection reason</label>
              <Input value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                placeholder="Explain why this campaign is rejected..." className="text-sm"
                data-testid={`reject-reason-${campaign.campaign_id}`} />
            </div>
            <Button size="sm" className="bg-red-600 hover:bg-red-700 h-9" onClick={() => {
              onReject(campaign.campaign_id, rejectReason);
              setShowReject(false);
              setRejectReason('');
            }} data-testid={`reject-confirm-${campaign.campaign_id}`}>
              Confirm Reject
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════

export default function AdminCampaignReview() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);

  const getToken = () => '';
  const authHeaders = () => ({ 'Content-Type': 'application/json' });

  const fetchData = useCallback(async () => {
    try {
      const [campRes, slotRes] = await Promise.all([
        api.get(`/admin/campaign-review`),
        api.get(`/admin/campaign-slots`),
      ]);
      if (campRes.ok) {
        const d = campRes.data;
        setCampaigns(d.campaigns || []);
      }
      if (slotRes.ok) {
        const d = slotRes.data;
        setSlots(d.slots || []);
      }
    } catch (err) {
      console.error('[AdminCampaignReview]', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApprove = async (id) => {
    await api.post(`/admin/campaign-review/${id}`, { action: 'approve' });
    fetchData();
  };

  const handleReject = async (id, reason) => {
    await api.post(`/admin/campaign-review/${id}`, { action: 'reject', reason });
    fetchData();
  };

  const handleAssignSlot = async (campaignId, slot) => {
    await api.post('/admin/campaign-slots/assign', { campaign_id: campaignId, slot });
    fetchData();
  };

  const handleUnassignSlot = async (campaignId) => {
    await api.post('/admin/campaign-slots/unassign', { campaign_id: campaignId });
    fetchData();
  };

  const handleRunScheduler = async () => {
    const res = await api.post('/admin/campaign-scheduler/run');
    const d = res.data;
    alert(`Scheduler ran: ${d.activated} activated, ${d.expired} expired`);
    fetchData();
  };

  const pendingCount = campaigns.filter(c => c.status === 'pending_review').length;
  const approvedNoSlot = campaigns.filter(c => ['approved', 'active'].includes(c.status) && !c.homepage_slot);
  const filtered = filter === 'all' ? campaigns : campaigns.filter(c => c.status === filter);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="admin-review-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#3BA4C5]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-campaign-review">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/treatx-admin/dashboard')} data-testid="back-btn">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-[#122033] flex items-center gap-2">
                <Shield className="h-6 w-6 text-[#3BA4C5]" /> Campaign Review
              </h1>
              <p className="text-sm text-slate-500">
                Review clinic campaigns &middot;
                {pendingCount > 0 && <span className="text-amber-600 font-medium"> {pendingCount} pending review</span>}
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={handleRunScheduler} className="text-xs gap-1" data-testid="run-scheduler-btn">
            <Clock className="h-3 w-3" /> Run Scheduler
          </Button>
        </div>

        {/* Slot Manager */}
        <SlotManager slots={slots} approvedCampaigns={approvedNoSlot} onAssign={handleAssignSlot} onUnassign={handleUnassignSlot} />

        {/* Filter */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          {[
            { v: 'all', l: `All (${campaigns.length})` },
            { v: 'pending_review', l: `Pending (${campaigns.filter(c => c.status === 'pending_review').length})` },
            { v: 'approved', l: 'Approved' },
            { v: 'active', l: 'Active' },
            { v: 'rejected', l: 'Rejected' },
            { v: 'expired', l: 'Expired' },
          ].map(f => (
            <button key={f.v} onClick={() => setFilter(f.v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === f.v ? 'bg-[#122033] text-white' : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-300'}`}
              data-testid={`filter-${f.v}`}>
              {f.l}
            </button>
          ))}
        </div>

        {/* Campaign List */}
        <div className="space-y-3">
          {filtered.map(c => (
            <ReviewCard key={c.campaign_id} campaign={c}
              onApprove={handleApprove} onReject={handleReject}
              expanded={expandedId === c.campaign_id}
              onToggle={() => setExpandedId(expandedId === c.campaign_id ? null : c.campaign_id)} />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16" data-testid="no-campaigns">
            <Megaphone className="h-12 w-12 text-slate-200 mx-auto mb-3" />
            <p className="text-lg font-medium text-slate-400">No campaigns found</p>
            <p className="text-sm text-slate-300">Campaigns submitted by clinics will appear here for review.</p>
          </div>
        )}
      </div>
    </div>
  );
}
